// Shared "is this campsite actually free?" rule.
//
// Listing data is frequently contradictory — a paid, bookable foreshore reserve
// can still carry a stray "Free Camping" feature (and even entity_type
// 'free_camping') while also having a booking_url and a "Camping Fee" feature.
// A naive `feature.desc.includes('free')` then mis-badges it FREE (and also
// matches "Free WiFi", "Smoke-free", etc.).
//
// Rule: anything bookable or with a camping fee is NOT free, full stop;
// otherwise require an explicit free-CAMPING signal.

interface FreeCheckEntity {
  booking_url?: string | null;
  entity_type?: string | null;
  features?: Array<{ desc?: string | null }> | null;
  /** Web-checked fee status from scripts/verify_freecamp_fees.py. */
  fee_verdict?: string | null;
}

/**
 * Verdicts that settle the question against the free claim. When the sweep
 * has looked a site up and found a fee, that outranks every heuristic below.
 *
 * Deliberately one-way: a contradicting verdict removes the badge, but a
 * missing verdict does NOT remove it. Requiring a FREE verdict here would
 * blank the badge on every genuinely free camp until the sweep finishes.
 */
const CONTRADICTS_FREE = new Set(['PAID', 'CLOSED', 'DONATION', 'PERMIT']);

export function isFreeEntity(entity: FreeCheckEntity | null | undefined): boolean {
  if (!entity) return false;
  if (CONTRADICTS_FREE.has((entity.fee_verdict || '').toUpperCase())) return false;
  if (entity.booking_url) return false;
  const feats = entity.features || [];
  const has = (s: string) => feats.some((f) => f.desc?.toLowerCase().includes(s));
  if (has('camping fee') || has('fees apply') || has('fee applies')) return false;
  return (
    has('free camping') ||
    has('free overnight') ||
    has('free camp') ||
    has('no cost') ||
    entity.entity_type === 'free_camping'
  );
}
